import { z } from "zod";
import moment from "moment-timezone";
import type { TFunction } from "i18next";
import { combineDateAndTime } from "../../../../../../../utils/helpers/date";
import type { UnavailableBlock } from "../../../../helpers/data/calculateUnavailableEvents";
import type { SelectedCellOrEvent } from "../../../../types/ScheduleState";
import type { Resource } from "../../../../types/Resource";
import type { DateFiltersMode } from "../../../../../../../components/shared/filters/DateFilters/types";

type OrgLevelGroup = {
    id: string;
    level: string;
    description: string;
    isRequired?: boolean;
};

type FormSection = {
    start?: Date;
    end?: Date;
    duration?: string;
    payCode?: any;
    job?: any;
    location?: any;
    type?: string;
    hasChanges?: { time: number; field: string };
    // Dynamic per-org-level-group keys (e.g. "level1", "customLevel"), computed from
    // `group.level.replaceAll("_", "")` - can't be named statically.
    [key: string]: any;
};

type EventValues = {
    date?: string | Date;
    users?: Resource[];
    isSimple: boolean;
    startDate?: Date;
    endDate?: Date;
    payCode?: any;
    job?: any;
    location?: any;
    sections?: FormSection[];
    recurrence?: {
        frequency?: string;
        interval?: number;
        endType?: "after" | "on";
        occurrences?: number;
        endDate?: Date;
        daysOfWeek?: string[];
    } | null;
    // Dynamic top-level per-org-level-group keys, used in Simple mode.
    [key: string]: any;
};

type CreateEventSchemaContext = {
    t: TFunction;
    intervals: UnavailableBlock[];
    mode: DateFiltersMode;
    selectedCellOrEvent?: SelectedCellOrEvent;
    allocatedGroups?: OrgLevelGroup[];
};

const validateDuration = (date: moment.MomentInput, time: moment.MomentInput, t: TFunction) => {
    let end = moment(date);
    const start = moment(time);

    if (end.isSameOrBefore(start, "seconds")) {
        end = end.add(1, "day");
    }

    const duration = end.diff(start, "seconds");

    if (duration > 86100) {
        return t("max-duration", { duration: "23:55" });
    }

    if (duration < 300) {
        return t("section-start-difference-from-shift-end");
    }

    return undefined;
};

export const overlaps = (
    aStart: moment.Moment,
    aEnd: moment.Moment,
    bStart: moment.Moment,
    bEnd: moment.Moment
) => {
    return aStart.isBefore(bEnd, "seconds") &&
        aEnd.isAfter(bStart, "seconds");
}

export const normalizeRange = (start: moment.Moment, end: moment.Moment) => {
    const normalizedEnd = end.isSameOrBefore(start, "seconds")
        ? end.clone().add(1, "day")
        : end;

    return { start, end: normalizedEnd };
};

type SectionValidationArgs = {
    value?: Date | string;
    index: number;
    sections: FormSection[];
    intervals: UnavailableBlock[];
    date?: Date | string;
    t: TFunction;
};

export const startValidation = ({
    value,
    index,
    sections,
    intervals,
    date,
    t,
}: SectionValidationArgs) => {
    if (!value) return t("required-start");

    const startValue = combineDateAndTime(moment(date), moment(value));

    const currentEnd = sections[index]?.end
        ? combineDateAndTime(moment(date), moment(sections[index].end))
        : undefined;

    const lastEnd =
        sections?.length > 1 && sections[sections.length - 1]?.end
            ? combineDateAndTime(
                moment(date),
                moment(sections[sections.length - 1].end)
            )
            : undefined;

    // 1️⃣ Duration validation
    const durationError = currentEnd
        ? validateDuration(currentEnd, startValue, t)
        : lastEnd
            ? validateDuration(lastEnd, startValue, t)
            : undefined;

    if (durationError) return durationError;

    // Determine section range for interval validation
    const sectionStart = startValue;
    const sectionEnd = currentEnd ?? lastEnd;

    // 2️⃣ Interval validation (range-based)
    if (sectionEnd && intervals?.length > 0) {
        const allDayNonWorkingTime = intervals.find(
            (i: UnavailableBlock) => i.allDay && !i.isAvailable
        );

        if (allDayNonWorkingTime) {
            return t("day-not-available");
        }

        const hasConflict = intervals
            .filter((i: UnavailableBlock) => !i.isAvailable)
            .some((interval: UnavailableBlock) => {
                let blockedStart = moment.parseZone(interval.startDate);
                let blockedEnd = moment.parseZone(interval.endDate);

                const normalizedBlocked = normalizeRange(blockedStart, blockedEnd);
                const normalizedSection = normalizeRange(sectionStart, sectionEnd);

                return overlaps(
                    normalizedSection.start,
                    normalizedSection.end,
                    normalizedBlocked.start,
                    normalizedBlocked.end
                );
            });

        if (hasConflict) {
            return t("event-invalid-time");
        }
    }

    return undefined;
};

export const endValidation = ({
    value,
    index,
    sections,
    intervals,
    date,
    t,
}: SectionValidationArgs) => {
    if (!value) return t("required-end");

    const endValue = combineDateAndTime(moment(date), moment(value));

    const currentStart = sections[index]?.start
        ? combineDateAndTime(moment(date), moment(sections[index].start))
        : undefined;

    const firstStart =
        sections?.length > 1 && sections[0]?.start
            ? combineDateAndTime(
                moment(date),
                moment(sections[0].start)
            )
            : undefined;

    // 1️⃣ Duration validation
    const durationError = currentStart
        ? validateDuration(endValue, currentStart, t)
        : firstStart
            ? validateDuration(endValue, firstStart, t)
            : undefined;
    if (durationError) return durationError;

    // Determine section range for interval validation
    const sectionStart = currentStart ?? firstStart;
    const sectionEnd = endValue;

    // 2️⃣ Interval validation (range-based)
    if (sectionStart && intervals?.length > 0) {
        const allDayNonWorkingTime = intervals.find(
            (i: UnavailableBlock) => i.allDay && !i.isAvailable
        );

        if (allDayNonWorkingTime) {
            return t("day-not-available");
        }

        const hasConflict = intervals
            .filter((i: UnavailableBlock) => !i.isAvailable)
            .some((interval: UnavailableBlock) => {
                const blockedStart = moment.parseZone(interval.startDate);
                const blockedEnd = moment.parseZone(interval.endDate);

                return overlaps(
                    sectionStart,
                    sectionEnd,
                    blockedStart,
                    blockedEnd
                );
            });

        if (hasConflict) {
            return t("event-invalid-time");
        }
    }

    return undefined;
};

type BreakItem = {
    start?: Date;
    end?: Date;
    payCode?: any;
};

type BreakValidationArgs = {
    value?: Date;
    index: number;
    breaks: BreakItem[];
    intervals: UnavailableBlock[];
    date?: Date | string;
    shiftStart?: Date;
    shiftEnd?: Date;
    t: TFunction;
};

const getShiftRange = (
    date: moment.MomentInput,
    shiftStart: moment.MomentInput,
    shiftEnd: moment.MomentInput
) => {
    const start = combineDateAndTime(moment(date), moment(shiftStart));
    let end = combineDateAndTime(moment(date), moment(shiftEnd));

    if (end.isSameOrBefore(start, "seconds")) {
        end = end.add(1, "day");
    }

    return { start, end };
};

const breakIntervalsValidation = (
    breakStart: moment.Moment,
    breakEnd: moment.Moment | undefined,
    intervals: UnavailableBlock[],
    t: TFunction
) => {
    if (!breakEnd || !intervals?.length) return undefined;

    const allDayNonWorkingTime = intervals.find(
        (i: UnavailableBlock) => i.allDay && !i.isAvailable
    );
    if (allDayNonWorkingTime) {
        return t("day-not-available");
    }

    const hasConflict = intervals
        .filter((i: UnavailableBlock) => !i.isAvailable)
        .some((interval: UnavailableBlock) => {
            const blockedStart = moment.parseZone(interval.startDate);
            const blockedEnd = moment.parseZone(interval.endDate);

            const normalizedBlocked = normalizeRange(blockedStart, blockedEnd);
            const normalizedBreak = normalizeRange(breakStart, breakEnd);

            return overlaps(
                normalizedBreak.start,
                normalizedBreak.end,
                normalizedBlocked.start,
                normalizedBlocked.end
            );
        });

    return hasConflict ? t("event-invalid-time") : undefined;
};

export const breakStartValidation = ({
    value,
    index,
    breaks,
    intervals,
    date,
    shiftStart,
    shiftEnd,
    t,
}: BreakValidationArgs) => {
    if (!value) return t("required-start");
    if (!shiftStart || !shiftEnd) return undefined;

    const shiftRange = getShiftRange(date, shiftStart, shiftEnd);
    let breakStart = combineDateAndTime(shiftRange.start, moment(value));
    if (breakStart.isBefore(shiftRange.start, "seconds")) {
        breakStart = breakStart.add(1, "day");
    }

    const endValue = breaks[index]?.end;
    let breakEnd = endValue ? combineDateAndTime(shiftRange.start, moment(endValue)) : undefined;
    if (breakEnd && breakEnd.isSameOrBefore(breakStart, "seconds")) {
        breakEnd = breakEnd.add(1, "day");
    }

    if (
        breakStart.isSameOrBefore(shiftRange.start, "seconds") ||
        (breakEnd && breakEnd.isSameOrAfter(shiftRange.end, "seconds"))
    ) {
        return t("break-outside-shift");
    }

    return breakIntervalsValidation(breakStart, breakEnd, intervals, t);
};

export const breakEndValidation = ({
    value,
    index,
    breaks,
    intervals,
    date,
    shiftStart,
    shiftEnd,
    t,
}: BreakValidationArgs) => {
    if (!value) return t("required-end");
    if (!shiftStart || !shiftEnd) return undefined;

    const shiftRange = getShiftRange(date, shiftStart, shiftEnd);

    const startValue = breaks[index]?.start;
    if (!startValue) return undefined;

    let breakStart = combineDateAndTime(shiftRange.start, moment(startValue));
    if (breakStart.isBefore(shiftRange.start, "seconds")) {
        breakStart = breakStart.add(1, "day");
    }
    let breakEnd = combineDateAndTime(shiftRange.start, moment(value));
    if (breakEnd.isSameOrBefore(breakStart, "seconds")) {
        breakEnd = breakEnd.add(1, "day");
    }

    if (
        breakStart.isSameOrBefore(shiftRange.start, "seconds") ||
        breakEnd.isSameOrAfter(shiftRange.end, "seconds")
    ) {
        return t("break-outside-shift");
    }

    return breakIntervalsValidation(breakStart, breakEnd, intervals, t);
};

export const createEventSchema = ({
    t,
    intervals,
    mode,
    selectedCellOrEvent,
    allocatedGroups,
}: CreateEventSchemaContext) => {
    const groups = allocatedGroups ?? [];
    const sectionIntervals = mode !== "month" ? intervals : [];

    return z
        .object({
            date: z.any(),
            users: z.any(),
            isSimple: z.boolean(),
            startDate: z.any(),
            endDate: z.any(),
            payCode: z.any(),
            job: z.any(),
            location: z.any(),
            sections: z.array(z.any()).optional(),
            breaks: z.array(z.any()).optional(),
            slots: z.any().optional(),
            recurrence: z.any().optional().nullable(),
        })
        .passthrough()
        .superRefine((values: EventValues, ctx) => {
            const isTemplate = !!selectedCellOrEvent?.isTemplate;
            const isUnassigned = !!selectedCellOrEvent?.isUnassignedShift;
            const showUsersField = mode === "month" &&
                selectedCellOrEvent?.type !== "unassigned" &&
                !isUnassigned;

            if (!values.date) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["date"],
                    message: isTemplate ? t("required-one-day") : t("required-date"),
                });
            }

            if (isUnassigned) {
                const slots = Number(values.slots);
                if (!Number.isInteger(slots) || slots < 1) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["slots"],
                        message: t("min-one-slot"),
                    });
                }
            }

            if (showUsersField && !(values.users?.length > 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["users"],
                    message: t("required-users"),
                });
            }

            if (values.isSimple) {
                const pseudoSections: FormSection[] = [{ start: values.startDate, end: values.endDate }];

                const startError = startValidation({
                    value: values.startDate,
                    index: 0,
                    sections: pseudoSections,
                    intervals: sectionIntervals,
                    date: values.date,
                    t,
                });
                if (startError) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["startDate"], message: startError });
                }

                const endError = endValidation({
                    value: values.endDate,
                    index: 0,
                    sections: pseudoSections,
                    intervals: sectionIntervals,
                    date: values.date,
                    t,
                });
                if (endError) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["endDate"], message: endError });
                }

                if (!values.payCode) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["payCode"], message: t("required-pay-code") });
                }

                if (!values.job) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["job"], message: t("required-job") });
                }

                if (!values.location) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["location"], message: t("required-location") });
                }

                groups.forEach((group: OrgLevelGroup) => {
                    const key = group.level.replaceAll("_", "");
                    if (!values[key]) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [key],
                            message: t("required-level", { level: group?.description }),
                        });
                    }
                });

                const canAddBreaks = !!(selectedCellOrEvent?.user as Resource)?.calculationGroup?.breaks?.manual?.status;
                // When manual breaks aren't enabled, `breaks` holds read-only automatic-break
                // entries (numeric second offsets, no payCode) instead of user-editable ones -
                // validating those against the manual-break rules always fails and blocks submit.
                const breaks = (canAddBreaks || isUnassigned) ? (values.breaks ?? []) : [];
                breaks.forEach((brk: BreakItem, index: number) => {
                    const startError = breakStartValidation({
                        value: brk?.start,
                        index,
                        breaks,
                        intervals: sectionIntervals,
                        date: values.date,
                        shiftStart: values.startDate,
                        shiftEnd: values.endDate,
                        t,
                    });
                    if (startError) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", index, "start"], message: startError });
                    }

                    const endError = breakEndValidation({
                        value: brk?.end,
                        index,
                        breaks,
                        intervals: sectionIntervals,
                        date: values.date,
                        shiftStart: values.startDate,
                        shiftEnd: values.endDate,
                        t,
                    });
                    if (endError) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", index, "end"], message: endError });
                    }

                    if (!brk?.payCode) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["breaks", index, "payCode"], message: t("required-pay-code") });
                    }
                });
            } else {
                const sections = values.sections ?? [];
                const hasAutomaticBreaks = (selectedCellOrEvent?.user as Resource)?.calculationGroup?.breaks?.automatic?.status;

                sections.forEach((section: FormSection, index: number) => {
                    const startError = startValidation({
                        value: section?.start,
                        index,
                        sections,
                        intervals: sectionIntervals,
                        date: values.date,
                        t,
                    });
                    if (startError) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "start"], message: startError });
                    }

                    const endError = endValidation({
                        value: section?.end,
                        index,
                        sections,
                        intervals: sectionIntervals,
                        date: values.date,
                        t,
                    });
                    if (endError) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "end"], message: endError });
                    }

                    const payCode = section?.payCode;
                    const isBreak = !!(payCode?.hourType === "unpaid" && payCode?.forBreak);
                    if (!payCode) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "payCode"], message: t("required-pay-code") });
                    } else if (hasAutomaticBreaks && isBreak && section?.type !== "break") {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "payCode"], message: t("cannot-add-break") });
                    }

                    if (!section?.duration) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "duration"], message: t("required-duration") });
                    }

                    if (!section?.job) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "job"], message: t("required-job") });
                    }

                    if (!section?.location) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["sections", index, "location"], message: t("required-location") });
                    }

                    groups.forEach((group: OrgLevelGroup) => {
                        const key = group.level.replaceAll("_", "");
                        if (!section?.[key]) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ["sections", index, key],
                                message: t("required-level", { level: group?.description }),
                            });
                        }
                    });
                });
            }

            if (values.recurrence) {
                const { endType, occurrences, endDate } = values.recurrence;
                if (endType === "after" && !occurrences) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recurrence", "occurrences"], message: t("is-required") });
                }
                if (endType === "on" && !endDate) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["recurrence", "endDate"], message: t("required-end-date") });
                }
            }
        });
};

export type EventSchema = ReturnType<typeof createEventSchema>;

export const createSelectTemplateSchema = ({ t }: { t: TFunction }) => {
    return z.object({
        template: z.any().optional(),
    }).superRefine((values: { template?: { id: string } }, ctx) => {
        if (!values.template) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["template"], message: t("required-template") });
        }
    });
};
